import { useCallback, useEffect, useState, type ReactElement } from "react";
import { useParams } from "react-router";
import { Box, Grid } from "@mui/material";
import { LogoWhite } from "@platform/portal-ui";
import { Alert, Typography } from "@platform/design-system";

import { loginWithAuth0 } from "../../auth/auth0Pkce";
import { LoginPage, LOGIN_PAGE_TEST_ID } from "../../components/loginPage";
import { ROUTE_NAMES } from "../../constants";

interface CentroLoginProps {
  connection?: string;
}

function CentroLogin({
  connection: propConnection,
}: CentroLoginProps = {}): ReactElement {
  const { connection: paramConnection } = useParams<{ connection: string }>();
  const connection = propConnection || paramConnection;
  const [, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleCentroLogin = useCallback(async (): Promise<void> => {
    // Prevent duplicate calls from non-login pages
    if (!window.location.pathname.toLowerCase().includes(ROUTE_NAMES.LOGIN)) {
      return;
    }

    if (!connection) {
      setError("Connection parameter is required");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await loginWithAuth0(connection);
    } catch {
      setLoading(false);
      setError("Failed to initiate login. Please try again.");
    }
  }, [connection]);

  useEffect(() => {
    // Auto-redirect to Auth0 login on component mount
    handleCentroLogin();
  }, [handleCentroLogin]);

  return (
    <LoginPage
      container
      data-testid={LOGIN_PAGE_TEST_ID}
      justifyContent="space-between"
    >
      <Grid size={3} margin={"auto 6rem"}>
        <Typography variant="display-medium" color="brandPrimary">
          Single Sign On
        </Typography>
        {error && (
          <Box mt={2} mb={2}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}
        <Box display="flex" flexDirection="column" gap={2} mb={2} mt={4}>
          <Typography variant="body-large">
            {connection
              ? `Signing you in to ${connection} authentication...`
              : "Invalid connection specified"}
          </Typography>
        </Box>
      </Grid>
      <Grid size={5} display="flex" alignItems="center" className="login-logo">
        <Box component="img" src={LogoWhite as string} alt="centro thumb" />
      </Grid>
    </LoginPage>
  );
}

export default CentroLogin;
